"use client";

import React from "react";
import { 
  X, 
  Stethoscope, 
  Mail, 
  Phone, 
  Calendar, 
  Activity, 
  UserCheck, 
  Clock, 
  CreditCard,
  Trash2,
  Shield
} from "lucide-react";
import { DentalPatientLead } from "@/lib/demos/dentalDb";

interface DentalDetailModalProps {
  patient: DentalPatientLead | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: number) => void;
}

export default function DentalDetailModal({
  patient,
  isOpen,
  onClose,
  onDelete,
}: DentalDetailModalProps) {
  if (!isOpen || !patient) return null;

  const sym = patient.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-slate-950 border border-slate-800 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative shadow-2xl text-slate-100 space-y-6">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-1.5 rounded-full hover:bg-slate-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-teal-500/10 border border-teal-500/30 text-[10px] font-extrabold text-teal-400 uppercase tracking-wider">
                {patient.stage}
              </span>
              <span className="text-xs text-slate-400">PRN-2026-00{patient.id}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white">
              {patient.patient_name}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 pt-1">
              <span className="flex items-center gap-1">
                <Mail className="w-3.5 h-3.5 text-slate-500" />
                {patient.email}
              </span>
              {patient.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 text-slate-500" />
                  {patient.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Clinical Dossier Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1.5 sm:col-span-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Primary Treatment Focus
            </span>
            <p className="font-semibold text-white text-sm">{patient.treatment_interest}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Attending Specialist Doctor
            </span>
            <p className="font-semibold text-teal-300">{patient.preferred_doctor}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Confirmed Scan / Chair Schedule
            </span>
            <p className="font-semibold text-emerald-400">{patient.scan_date || "Pending Confirmation"}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Insurance / Reimbursement
            </span>
            <p className="font-semibold text-white">{patient.insurance_provider || "Private Self-Pay"}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Estimated Clinical Fee ({patient.currency})
            </span>
            <p className="font-bold text-teal-400 text-sm">
              {sym}{patient.estimated_cost.toLocaleString()}
            </p>
          </div>
        </div>

        {patient.clinical_notes && (
          <div className="p-3.5 rounded-2xl bg-slate-900/40 border border-slate-800 text-xs space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Clinical Findings &amp; Patient Notes:
            </span>
            <p className="text-slate-300">{patient.clinical_notes}</p>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-800 text-xs">
          <button
            onClick={() => {
              if (confirm(`Archive clinical record for "${patient.patient_name}"?`)) {
                onDelete(patient.id);
                onClose();
              }
            }}
            className="text-red-400 hover:text-red-300 font-semibold flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Archive Patient Record</span>
          </button>

          <button
            onClick={onClose}
            className="py-2.5 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 font-bold text-white transition-colors"
          >
            Close EHR Dossier
          </button>
        </div>
      </div>
    </div>
  );
}
